import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  EA_STATUS_OPTIONS,
  canDeleteRequest,
  canEditRequest,
  deleteEAttestationRequest,
  findAllEAttestationRequests,
  getApproverDisplay,
  getEAttestationReqId,
  getStatusBadgeClass,
  getStatusCode,
  getStatusLabel,
  pick,
  toDisplayDate,
} from "./eAttestation.service";

const EAttestationList: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [confirmItem, setConfirmItem] = useState<any>(null);

  const loadList = useCallback(
    async (statusFilter = status) => {
      setLoading(true);
      try {
        const { list } = await findAllEAttestationRequests({
          page: 1,
          limit: 100,
          status: statusFilter,
        });
        setItems(Array.isArray(list) ? list : []);
      } catch (err: any) {
        setItems([]);
        toast.error(Api.getErrorMessage(err) || "Failed to load E-Attestation requests");
      } finally {
        setLoading(false);
      }
    },
    [status]
  );

  useEffect(() => {
    loadList("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (pick(item, "Deleted_at", "deleted_at")) return false;
      if (!fromDate && !toDate) return true;
      const created = String(pick(item, "Created_at", "created_at") || "");
      const date = created ? new Date(created) : null;
      if (!date || isNaN(date.getTime())) return true;
      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        if (date < from) return false;
      }
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        if (date > to) return false;
      }
      return true;
    });
  }, [items, fromDate, toDate]);

  const handleDelete = async () => {
    const id = pick(confirmItem, "Id", "id");
    if (!id) return;
    setDeletingId(id);
    try {
      const res: any = await deleteEAttestationRequest(id);
      toast.success(res?.message || res?.data?.message || "Request deleted successfully");
      setConfirmItem(null);
      await loadList();
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Failed to delete request");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">E-Attestation Process</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  E-Attestation Process
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Link to={routes.eAttestation.create} className="btn btn-danger">
              <i className="ti ti-plus me-1" />
              Create Request
            </Link>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  max={toDate || new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  min={fromDate || undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">Filter by Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStatus(value);
                    loadList(value);
                  }}
                >
                  {EA_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.key || "all"} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger flex-fill"
                  onClick={() => loadList()}
                  disabled={loading}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setStatus("");
                    loadList("");
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">No records found</div>
          </div>
        ) : (
          <div className="row g-3">
            {filtered.map((item) => {
              const id = pick(item, "Id", "id");
              const reqid = getEAttestationReqId(item);
              const lookupId = reqid || String(id || "");
              const showEdit = canEditRequest(item);
              const showDelete = canDeleteRequest(item);
              return (
                <div className="col-xl-6" key={lookupId || id}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Request ID: {reqid || "-"}</span>
                      <span className={getStatusBadgeClass(item)}>{getStatusLabel(item)}</span>
                    </div>
                    <div className="card-body">
                      <InfoRow label="Employee ID" value={pick(item, "EmpID", "empId") || "-"} />
                      <InfoRow
                        label="Name"
                        value={`${pick(item, "Firstname", "firstname") || ""} ${pick(item, "Lastname", "lastname") || ""}`.trim() || "-"}
                      />
                      <InfoRow label="Mobile" value={pick(item, "MobileNo", "mobileNo") || "-"} />
                      <InfoRow
                        label="Created On"
                        value={toDisplayDate(pick(item, "Created_at", "created_at"))}
                      />
                      <InfoRow
                        label="Current Approver"
                        value={getApproverDisplay(item)}
                      />
                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-danger flex-fill"
                          onClick={() =>
                            navigate(
                              routes.eAttestation.details.replace(":id", encodeURIComponent(lookupId))
                            )
                          }
                        >
                          View Details
                        </button>
                        {showEdit ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger flex-fill"
                            onClick={() =>
                              navigate(
                                routes.eAttestation.edit.replace(":id", encodeURIComponent(lookupId))
                              )
                            }
                          >
                            {getStatusCode(item) === "D" ? "Continue Draft" : "Edit & Resubmit"}
                          </button>
                        ) : null}
                        {showDelete ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setConfirmItem(item)}
                            disabled={deletingId === id}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {confirmItem ? (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Request</h5>
                  <button type="button" className="btn-close" onClick={() => setConfirmItem(null)} />
                </div>
                <div className="modal-body">
                  Are you sure you want to delete request {pick(confirmItem, "Reqid", "reqid") || pick(confirmItem, "Id", "id")}?
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={() => setConfirmItem(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={!!deletingId}
                    onClick={handleDelete}
                  >
                    {deletingId ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default EAttestationList;
